import { Task } from "../../types/task";
import { LangService } from "../lang/lang.service";
import { timeFormat, addLeadingZero, timeConvert } from "../../helpers";
import moment from "moment";
import 'moment/locale/de';
import { Employee } from "src/types/employee";
import { User } from "src/types/user";

export class MapperService {
  private langService: LangService = new LangService();

  private filter = (task: any, filters: any) => {
    const filteredFilters = filters.filter((item: any) => {
      return item['nameDe'] === task || item['nameEn'] === task
    });
    if (filteredFilters.length) {
      return { name: task, icon: filteredFilters[0].icon }
    }
  }

  public employeeReviews(stats: any) {
    return stats.reviews.map((review: any) => {
      review.building = review.building.name || 'building';
      review.total = 5;
      review.date = review.date || new Date();
      return review;
    });
  }

  public buildings = (buildingsDetails: any): any[] => {
    const outputBuildings = buildingsDetails.results.map((item: any, index: any) => {
      const timeFallback = { hours:0, minutes: 0 };
      const results = buildingsDetails.results[index]!.statistics! || {weekly:{}, monthly:{}};
      let {
        workingHours: weeklyWorkingHours,
        tasksCount: weeklyTasksCount,
        employeesCount: weeklyEmployees 
      } = results!.weekly!;

      let {
        workingHours: monthlyWorkingHours,
        tasksCount: monthleyTasksCount,
        employeesCount: monthlyEmployees 
      } = results!.monthly!;

      weeklyWorkingHours = weeklyWorkingHours || {}
      monthlyWorkingHours = monthlyWorkingHours || {}

      weeklyWorkingHours.hours = weeklyWorkingHours.hours || 0;
      weeklyWorkingHours.minutes = weeklyWorkingHours.minutes || 0;
      monthlyWorkingHours.hours = monthlyWorkingHours.hours || 0;
      monthlyWorkingHours.minutes = monthlyWorkingHours.minutes || 0;

      return {
        id: buildingsDetails.results[index].id,
        name: buildingsDetails.results[index].name,
        address: buildingsDetails.results[index].address,
        workingHours: {
          weekly: timeFormat(weeklyWorkingHours || timeFallback),
          monthly: timeFormat(monthlyWorkingHours || timeFallback),
          sortParam: ((weeklyWorkingHours.hours || 0) + ((weeklyWorkingHours.minutes || 0) / 60)) + ((monthlyWorkingHours.hours || 0) + ((monthlyWorkingHours.minutes || 0) / 60)),
        },
        tasksCount: {
          weekly: weeklyTasksCount || 0,
          monthly: monthleyTasksCount || 0,
          sortParam: (weeklyTasksCount || 0) + (monthleyTasksCount || 0),
        },
        employeesCount: {
          weekly: weeklyEmployees || 0,
          monthly: monthlyEmployees || 0,
          sortParam: (weeklyEmployees || 0) + (monthlyEmployees || 0),
        },
      }
    })
    return outputBuildings;
  }

  public tasks = async (data: any, activeFilter: any): Promise<any> => {
    try {
      const output: any[] = [];
      const filters: any[] = [];
      const tasksIDs: any[] = [];
      const userImage = new User().image;
      data.forEach((item: any) => {
        let matched = false;
        const newTask = new Task();
        item.tasks.forEach((task: any) => {
          const translations = task.translation;
          const currentLang = this.langService.getCurrentLang();
          task.name = translations[currentLang];
          task.nameEn = translations.en;
          task.nameDe = translations.de;
          newTask.tasks.push(task);
          if (! tasksIDs.includes(task.id)) {
            tasksIDs.push(task.id)
            filters.push(task);
          }
          if((activeFilter.length && activeFilter.includes(task.name)) || activeFilter.length === 0) {
            matched = true;
          }
        });
        newTask.date = item.time;
        newTask.user = item.user;
        newTask.user.image = item.user.image ? item.user.image : userImage;
        if (matched) {
          output.push(newTask);
        }
      });
      return { allTasks: output, filters };
    } catch (err) {
      return [];
    }
  };

  public mobTasks = async (data: any, activeFilter: any): Promise<any> => {
    try {
      const colorsPallete: any[] = [
        {
          color: "rgb(9, 93, 177)",
          lightIcon: true
        },
        {
          color: "rgb(51, 73, 94)",
          lightIcon: true
        },
        {
          color: "rgb(232, 239, 247)",
          lightIcon: false
        },
        {
          color: "rgb(142, 198, 247)",
          lightIcon: false
        },
        {
          color: "rgb(26, 131, 212)",
          lightIcon: true
        }
      ];
      const filters: any[] = [];
      const tasksIDs: any[] = [];
      const tasksByDate: any = {};
      const userImage = new User().image;
      data.forEach((item: any) => {
        let matched = false;
        const newTask: any = {
          tasks: []
        };
        let colorIndex: number = 0;
        item.tasks.forEach((task: any) => {
          const translations = task.translation;
          const currentLang = this.langService.getCurrentLang();
          task.name = translations[currentLang];
          task.nameEn = translations.en;
          task.nameDe = translations.de;
          task.user = item.user;
          task.user.image = item.user.image ? item.user.image : userImage;
          task.date = item.time;
          task.color = colorsPallete[colorIndex].color;
          task.lightIcon = colorsPallete[colorIndex].lightIcon;
          newTask.tasks.push(task);
          if (!tasksIDs.includes(task.id)) {
            tasksIDs.push(task.id)
            filters.push(task);
          }
          if((activeFilter.length && activeFilter.includes(task.name)) || activeFilter.length === 0) {
            matched = true;
          }
          colorIndex = (colorIndex === (colorsPallete.length - 1)) ? 0 : (colorIndex + 1);
        });
        if (matched) {
          const date = moment(new Date(item.time)).format("YYYY-MM-DD");
          if (tasksByDate[date] && tasksByDate[date].tasks) {
            tasksByDate[date].tasks = [...tasksByDate[date].tasks, ...newTask.tasks];
          }
          tasksByDate[date] = newTask;
        }
      });
      return { allTasks: tasksByDate, filters };
    } catch (err) {
      return [];
    }
  };

  public tableTasks = (tasks: any[]) => {
    return tasks.map((task: any) => {
      task.nameDe = task.translation.de;
      task.nameEn = task.translation.en;
      return task;
    });
  }

  public rating = (date: any) => {
    return {};
  }

  public workingHoursChart = (workingHours: any[], filter: string) => {
    const labels: any[] = [];
    const data: any[] = [];
    const lang = this.langService.getCurrentLang();
    workingHours.map((item: any) => {
      const value: any = (item.value / 60) || '0';
      filter.includes('Week') ? labels.push(moment(new Date(item.key)).locale(lang).format('dd')) : labels.push(item.key);
      data.push( value ? value.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0] : value);
      return item;
    });
    return { labels, data };
  }

  public tasksChart = (tasks: any[]) => {
    const data: any[] = [];
    tasks.map((item: any) => {
      data.push(item.value ? Number(item.value.toFixed(2)) : item.value);
      return item;
    });
    return data;
  }

  public activitiesChart = (activities: any[] = []) => {
    const labels: any[] = [];
    const data: any[] = [];
    const colors: any[] = [];
    const colorsPallete: string[] = [
      "rgb(26, 131, 212)",
      "rgb(51, 73, 94)",
      "rgb(58, 169, 255)",
      "rgb(9, 93, 177)",
      "rgb(232, 239, 247)",
      "rgb(142, 198, 247)",
      "rgb(210, 230, 252)",
      "rgb(4, 62, 98)",
      "rgb(84, 185, 248)",
      "rgb(182, 225, 252)",
      "rgb(122, 154, 184)",
      "rgb(172, 192, 210)",
      "rgb(205, 218, 228)"
    ];
    const lang = this.langService.getCurrentLang();
    let totalHours = 0;
    let totalMinutes = 0;
    let colorIndex = 0;
    activities.map((item: any) => {
      labels.push(item.translation[lang]);
      data.push(item.workingHours.hours + (item.workingHours.minutes / 60));
      colors.push(colorsPallete[colorIndex]);
      totalHours += item.workingHours.hours;
      totalMinutes += item.workingHours.minutes;
      colorIndex = (colorIndex + 1) === colorsPallete.length ? 0 : (colorIndex + 1);
      return item;
    });
    const { rhours, rminutes } = timeConvert(totalMinutes);
    totalHours += rhours;
    totalMinutes = rminutes;
    const total = `${addLeadingZero(totalHours)}:${addLeadingZero(totalMinutes)}`;
    return { labels, data, colors, total };
  }

  public employees = async (data: any, activeFilter: any): Promise<any> => {
    try {
      const output: any[] = [];
      const filtersSet: any = new Set();
      data.forEach((item: any) => {
        let matched = false;
        const newEmployee = new Employee();
        newEmployee.user = new User();
        if(item.buildings) {
          item.buildings.forEach((building: any) => {
            const name = building.name;
            filtersSet.add(name);
            newEmployee.locations.push(name);
            if((activeFilter.length && activeFilter.includes(name))) {
              matched = true;
            }
          });
        }
        if(activeFilter.length === 0) {
          matched = true;
        }
        newEmployee.workingHours = item.workingHours || {hours: '00', minutes: '00'};
        newEmployee.activities = item.tasks || '0';
        newEmployee.user.firstName = item.name;
        newEmployee.user.id = item.sub;
        newEmployee.user.lastName = item.family_name;
        newEmployee.user.image = item.imageURL ? item.imageURL : newEmployee.user.image;
        newEmployee.rating = Math.ceil(item.rate);
        if (matched) {
          output.push(newEmployee);
        }
      });
      return { allEmployees: output, filters: [...filtersSet] };
    } catch (err) {
      return [];
    }
  }
}
